import { Edit } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface ServiceChecklist {
  id: number;
  service_id: number;
  name: string;
  description: string;
  is_template: boolean;
  display_order: number;
  is_active: boolean;
  wall_by_wall?: boolean;
}

interface EditChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { name: string; description: string; wall_by_wall: boolean }) => void;
  checklist: ServiceChecklist;
  isUpdating: boolean;
}

export function EditChecklistModal({
  isOpen,
  onClose,
  onConfirm,
  checklist,
  isUpdating,
}: EditChecklistModalProps) {
  const [checklistName, setChecklistName] = useState("");
  const [checklistDescription, setChecklistDescription] = useState("");
  const [wallByWallEnabled, setWallByWallEnabled] = useState(false);
  const nameFieldId = useId();
  const descriptionFieldId = useId();
  const wallByWallFieldId = useId();

  useEffect(() => {
    if (checklist) {
      setChecklistName(checklist.name);
      setChecklistDescription(checklist.description || "");
      setWallByWallEnabled(checklist.wall_by_wall);
    }
  }, [checklist]);

  const handleSubmit = () => {
    if (!checklistName.trim()) {
      return;
    }

    onConfirm({
      name: checklistName.trim(),
      description: checklistDescription.trim(),
      wall_by_wall: wallByWallEnabled,
    });
  };

  const handleClose = () => {
    onClose();
    // Reset form when modal closes
    if (checklist) {
      setChecklistName(checklist.name);
      setChecklistDescription(checklist.description || "");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Checklist
          </DialogTitle>
          <DialogDescription>Update the checklist details below.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor={nameFieldId}>Checklist Name</Label>
            <Input
              id={nameFieldId}
              value={checklistName}
              onChange={(e) => setChecklistName(e.target.value)}
              placeholder="e.g., Removal and disposal of tiles"
              disabled={isUpdating}
            />
          </div>
          <div>
            <Label htmlFor={descriptionFieldId}>Description (Optional)</Label>
            <Textarea
              id={descriptionFieldId}
              value={checklistDescription}
              onChange={(e) => setChecklistDescription(e.target.value)}
              placeholder="Describe this checklist..."
              disabled={isUpdating}
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Checkbox
              id={wallByWallFieldId}
              checked={wallByWallEnabled}
              onCheckedChange={(checked) => setWallByWallEnabled(checked === true)}
              disabled={isUpdating}
            />
            <div className="flex-1">
              <Label htmlFor={wallByWallFieldId} className="cursor-pointer font-medium">
                Enable wall-by-wall area calculator
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Displays a calculator at the top of this checklist for calculating wall and floor
                areas
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isUpdating || !checklistName.trim()}>
            {isUpdating ? "Updating..." : "Update Checklist"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
